import { createSelector } from "@reduxjs/toolkit";

import { selectMultiImages } from "../../../components/ImageUploader/images.slice";
import { FormId } from "../../../components/enums";
import { UIState } from "../../../store/HOC/genericUI.slice";
import { RootState } from "../../../store/rootReducer";
import { generateAddress, generatePhone } from "../../../utilities/forms";

import { ComponentId as CompanyComponentId } from "./CompanyData/enums";
import { ComponentId as GeneralComponentId } from "./GeneralData/enums";
import { ComponentId as OwnerComponentId } from "./OwnerDetails/enums";
import { ComponentId as PerpetratorComponentId } from "./PerpetratorData/enums";
import {
  FormDataInputs,
  PerpetratorFormInputs,
  PerpetratorGeneralFormInputs,
  PerpetratorCompanyDataFormInputs,
  PerpetratorOwnerFormInputs,
} from "./perpetrator.slice";

const selectPerpetratorId = (state: RootState) =>
  state.ac.perpetrator.data[FormId.PERPETRATOR_GENERAL][
    GeneralComponentId.PERPETRATOR_GENERAL_ID
  ];

const selectCarOwner = (state: RootState) =>
  state.ac.perpetrator.data[FormId.PERPETRATOR_GENERAL][
    GeneralComponentId.PERPETRATOR_GENERAL_CAR_OWNER
  ];

const selectWasDriving = (state: RootState) =>
  state.ac.perpetrator.data[FormId.PERPETRATOR_GENERAL][
    GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVING
  ];

export const selectPerpetratorGeneralData = (
  state: RootState
): PerpetratorGeneralFormInputs =>
  state.ac.perpetrator.data[FormId.PERPETRATOR_GENERAL];

export const selectPerpetratorData = (
  state: RootState
): PerpetratorFormInputs => state.ac.perpetrator.data[FormId.PERPETRATOR_DATA];

export const selectPerpetratorOwnerData = (
  state: RootState
): PerpetratorOwnerFormInputs =>
  state.ac.perpetrator.data[FormId.PERPETRATOR_OWNER];

export const selectPerpetratorCompanyData = (
  state: RootState
): PerpetratorCompanyDataFormInputs =>
  state.ac.perpetrator.data[FormId.PERPETRATOR_COMPANY_DATA];

export const selectPerpetratorWasDriver = createSelector(
  selectPerpetratorGeneralData,
  (generalData) =>
    generalData[
      GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_PERPETRATOR
    ] === "true"
);

export const selectPerpetratorWasNotDriver = createSelector(
  selectPerpetratorGeneralData,
  (generalData) =>
    generalData[
      GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_PERPETRATOR
    ] === "false"
);

export const selectOwnerPerpetratorInsuranceCompanyValue = createSelector(
  selectPerpetratorOwnerData,
  (ownerData) =>
    ownerData[OwnerComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY] ===
    "Wpisz innego ubezpieczyciela"
);

//console.log(OwnerComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY);

const selectPerpetratorWasSober = createSelector(
  selectPerpetratorGeneralData,
  (generalData) =>
    generalData[GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_SOBER] ===
    "false"
);
//console.log(GeneralComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_SOBER);

export const selectPerpetratorIsKnown = createSelector(
  selectPerpetratorGeneralData,
  (generalData) =>
    generalData[GeneralComponentId.PERPETRATOR_GENERAL_IS_KNOWN] === "true"
);

export const selectPerpetratorWasDriving = createSelector(
  selectWasDriving,
  (wasDriving) => wasDriving === "true"
);

export const selectPerpetratorWasNotDriving = createSelector(
  selectWasDriving,
  (wasDriving) => wasDriving === "false"
);

export const selectIsCompanyNotOwner = createSelector(
  selectCarOwner,
  (carOwner) => carOwner !== "company" && carOwner !== ""
);

export const selectIsCarOwnerDefined = createSelector(
  selectCarOwner,
  (carOwner) => carOwner !== ""
);

export const shouldDisplayCompanyOwner = createSelector(
  selectPerpetratorId,
  (id) => id !== "" && id === "company"
);

export const shouldDisplayPerpetratorAddress = createSelector(
  selectPerpetratorId,
  (id) => id !== "" && id === "person"
);

export const shouldDisplayPerpetratorAddressWhenDriving = createSelector(
  selectPerpetratorWasDriving,
  (wasDriving) => wasDriving
);

export const shouldDisplayCompanyPerpetrator = createSelector(
  selectCarOwner,
  (carOwner) => carOwner !== "" && carOwner === "company"
);

export const shouldDisplayPerpetratorPerson = createSelector(
  selectPerpetratorId,
  selectPerpetratorWasDriving,
  (id, wasDriving) => wasDriving || (!wasDriving && id === "person")
);

const selectPerpetratorNameSurname = createSelector(
  selectPerpetratorData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[PerpetratorComponentId.PERPETRATOR_NAME],
      data[PerpetratorComponentId.PERPETRATOR_SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectPerpetratorEmail = createSelector(
  selectPerpetratorData,
  (data) => ({
    name: "Email",
    value: data[PerpetratorComponentId.PERPETRATOR_EMAIL],
  })
);

const selectPerpetratorPhone = createSelector(
  selectPerpetratorData,
  (data) => ({
    name: "Tel.",
    value: generatePhone(data[PerpetratorComponentId.PERPETRATOR_PHONE]),
  })
);

const selectPerpetratorPESEL = createSelector(
  selectPerpetratorData,
  (data) => ({
    name: "PESEL",
    value: data[PerpetratorComponentId.PERPETRATOR_PESEL],
  })
);

const selectPerpetratorAddressData = createSelector(
  selectPerpetratorData,
  (data) => ({
    name: "Miejsce zamieszkania",
    value: generateAddress(data),
  })
);

const selectPerpetratorDetailsData = createSelector(
  selectPerpetratorNameSurname,
  selectPerpetratorEmail,
  selectPerpetratorPhone,
  selectPerpetratorPESEL,
  selectPerpetratorAddressData,
  (nameSurname, email, tel, pesel, address) => {
    return [nameSurname, email, tel, pesel, address];
  }
);

const selectCompanyDetailsData = createSelector(
  selectPerpetratorCompanyData,
  (data) => [
    {
      name: "Nazwa firmy",
      value: data[CompanyComponentId.PERPETRATOR_COMPANY_NAME],
    },
    {
      name: "REGON",
      value: data[CompanyComponentId.PERPETRATOR_COMPANY_REGON],
    },
    {
      name: "Tel.",
      value: generatePhone(data[CompanyComponentId.PERPETRATOR_COMPANY_PHONE]),
    },
    {
      name: "Email",
      value: data[CompanyComponentId.PERPETRATOR_COMPANY_EMAIL],
    },
  ]
);

const selectCompanyAddressData = createSelector(
  selectPerpetratorCompanyData,
  (data) => ({
    name: "Adres",
    value: generateAddress(data),
  })
);

const selectPerpetratorInsurance = createSelector(
  selectPerpetratorCompanyData,
  selectPerpetratorData,
  selectPerpetratorId,
  (dataCompany, dataPerson, id) => {
    if (id === "person") {
      return [
        {
          name: "Numer polisy OC",
          value: dataPerson[PerpetratorComponentId.PERPETRATOR_OC_NUMBER],
        },
        {
          name: "Towarzystwo ubezpieczeniowe",
          value:
            dataPerson[PerpetratorComponentId.PERPETRATOR_INSURANCE_COMPANY],
        },
      ];
    }

    return [
      {
        name: "Numer polisy OC",
        value: dataCompany[CompanyComponentId.PERPETRATOR_COMPANY_OC_NUMBER],
      },
      {
        name: "Towarzystwo ubezpieczeniowe",
        value:
          dataCompany[CompanyComponentId.PERPETRATOR_COMPANY_INSURANCE_COMPANY],
      },
    ];
  }
);

const selectPerpetratorWasDriverData = createSelector(
  selectPerpetratorDetailsData,
  (perpetratorPersonData) => perpetratorPersonData
);

const selectPerpetratorWasNotDriverData = createSelector(
  selectPerpetratorId,
  selectPerpetratorDetailsData,
  selectCompanyDetailsData,
  selectCompanyAddressData,
  selectPerpetratorInsurance,
  (
    id,
    perpetratorPersonData,
    companyDetailsData,
    companyAddressData,
    insurance
  ) => {
    if (id === "person") {
      return [...perpetratorPersonData, ...insurance];
    }

    return [...companyDetailsData, companyAddressData, ...insurance];
  }
);

const selectPerpetratorSummaryData = createSelector(
  selectPerpetratorWasDriving,
  selectPerpetratorWasDriverData,
  selectPerpetratorWasNotDriverData,
  (wasDriving, perpetratorDriverData, perpetratorNotDriverData) => {
    if (wasDriving) {
      return [...perpetratorDriverData];
    }

    return [...perpetratorNotDriverData];
  }
);

const selectGeneralData = createSelector(
  selectPerpetratorIsKnown,
  selectPerpetratorWasDriver,
  selectPerpetratorWasSober,
  selectPerpetratorSummaryData,
  (isPerpetratorKnown, wasPerpetratorDriver, wasSober, perpetratorData) => {
    if (!isPerpetratorKnown && !wasPerpetratorDriver) {
      return [
        {
          name: "Imię i nazwisko",
          value: "Sprawca nie jest znany",
        },
      ];
    }

    if (wasPerpetratorDriver) {
      return [
        {
          name: "Imię i nazwisko",
          value: "Osoba kierująca autem Ayvens",
        },
        {
          name: "Obecność używek w organizmie",
          value: wasSober ? "tak" : "nie",
        },
      ];
    }

    return [...perpetratorData];
  }
);

const selectCarDetails = createSelector(selectPerpetratorOwnerData, (data) => [
  {
    name: "Numer rejestracyjny",
    value: data[OwnerComponentId.PERPETRATOR_OWNER_REGISTRATION_NUMBER],
  },
  {
    name: "Marka",
    value: data[OwnerComponentId.PERPETRATOR_OWNER_MAKE],
  },
  {
    name: "Model",
    value: data[OwnerComponentId.PERPETRATOR_OWNER_MODEL],
  },
  {
    name: "Numer polisy OC",
    value: data[OwnerComponentId.PERPETRATOR_OWNER_OC_NUMBER],
  },
  {
    name: "Towarzystwo ubezpieczeniowe",
    value: data[OwnerComponentId.PERPETRATOR_OWNER_INSURANCE_COMPANY],
  },
]);

const selectPerpetratorDocuments = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: OwnerComponentId.PERPETRATOR_OWNER_DOCUMENTS,
    }),
  (data) => ({
    name: "Zdjęcia dokumentów",
    value: Object.values(data).map((image) => image.src as string),
  })
);

const selectOwnerNameSurname = createSelector(
  selectPerpetratorOwnerData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[OwnerComponentId.PERPETRATOR_OWNER_NAME],
      data[OwnerComponentId.PERPETRATOR_OWNER_SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectOwnerEmail = createSelector(selectPerpetratorOwnerData, (data) => ({
  name: "Email",
  value: data[OwnerComponentId.PERPETRATOR_OWNER_EMAIL],
}));

const selectOwnerPhone = createSelector(selectPerpetratorOwnerData, (data) => ({
  name: "Tel.",
  value: generatePhone(data[OwnerComponentId.PERPETRATOR_OWNER_PHONE]),
}));

const selectOwnerAddress = createSelector(
  selectPerpetratorOwnerData,
  (data) => ({
    name: "Miejsce zamieszkania",
    value: generateAddress(data),
  })
);

const selectPerpetratorOwnerDetailsData = createSelector(
  selectOwnerNameSurname,
  selectOwnerEmail,
  selectOwnerPhone,
  selectOwnerAddress,
  (nameSurname, email, phone, address) => [nameSurname, email, phone, address]
);

const selectCarData = createSelector(
  selectIsCompanyNotOwner,
  selectCarDetails,
  selectPerpetratorDocuments,
  (isNotCompany, carData, documents) => {
    if (!isNotCompany) {
      return [...carData];
    }

    return [...carData, documents];
  }
);

const selectOwnerData = createSelector(
  selectPerpetratorWasNotDriving,
  selectPerpetratorIsKnown,
  selectIsCompanyNotOwner,
  selectPerpetratorOwnerDetailsData,
  selectCompanyDetailsData,
  selectCompanyAddressData,
  (
    wasNotDriving,
    isPerpetratorKnown,
    isCompanyNotOwner,
    perpetratorOwnerData,
    companyDetailsData,
    companyAddressData
  ) => {
    if (wasNotDriving || !isPerpetratorKnown) {
      return [];
    }

    if (isCompanyNotOwner) {
      return [...perpetratorOwnerData];
    }

    return [...companyDetailsData, companyAddressData];
  }
);

export const selectSummaryData = createSelector(
  selectGeneralData,
  selectOwnerData,
  selectCarData,
  (perpetrator, owner, car) => ({
    perpetrator,
    owner,
    car,
  })
);

export const selectFormData = (store: RootState): FormDataInputs =>
  store.ac.perpetrator.data;

export const selectUIData = (store: RootState): UIState =>
  store.ac.perpetrator.ui;

export const selectPerpetratorAvailableCities = (store: RootState) =>
  store.ac.perpetrator.ui.perpetratorAvailableCities || [];

export const selectPerpetratorAvailableModels = (store: RootState) =>
  store.ac.perpetrator.ui.perpetratorAvailableModels || [];
